import { useCallback, useEffect, useRef } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 3;

/**
 * Pinch-to-zoom + pan for mobile PNL hero content. No-op when `enabled` is false (desktop).
 */
export function useMobilePinchZoom(enabled) {
  const containerRef = useRef(null);
  const stateRef = useRef({ scale: 1, x: 0, y: 0 });
  const gestureRef = useRef({
    mode: null,
    startDist: 0,
    startScale: 1,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
  });
  const isZoomedRef = useRef(false);

  const applyTransform = useCallback((scale, x, y) => {
    const el = containerRef.current;
    if (!el) return;
    if (scale === 1 && x === 0 && y === 0) {
      el.style.transform = "";
    } else {
      el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }
  }, []);

  const reset = useCallback(() => {
    stateRef.current = { scale: 1, x: 0, y: 0 };
    isZoomedRef.current = false;
    applyTransform(1, 0, 0);
  }, [applyTransform]);

  useEffect(() => {
    if (!enabled) {
      reset();
      return undefined;
    }

    const el = containerRef.current;
    if (!el) return undefined;

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (event) => {
      const { touches } = event;
      const gesture = gestureRef.current;
      const state = stateRef.current;

      if (touches.length === 2) {
        event.stopPropagation();
        gesture.mode = "pinch";
        gesture.startDist = getDistance(touches);
        gesture.startScale = state.scale;
        gesture.startX = state.x;
        gesture.startY = state.y;
        return;
      }

      if (touches.length === 1 && state.scale > 1) {
        event.stopPropagation();
        gesture.mode = "pan";
        gesture.lastX = touches[0].clientX;
        gesture.lastY = touches[0].clientY;
        gesture.startX = state.x;
        gesture.startY = state.y;
      }
    };

    const onTouchMove = (event) => {
      const gesture = gestureRef.current;
      const { touches } = event;

      if (gesture.mode === "pinch" && touches.length >= 2) {
        event.preventDefault();
        event.stopPropagation();

        const dist = getDistance(touches);
        const nextScale = Math.min(
          MAX_SCALE,
          Math.max(MIN_SCALE, gesture.startScale * (dist / gesture.startDist)),
        );
        const next = { scale: nextScale, x: gesture.startX, y: gesture.startY };
        stateRef.current = next;
        isZoomedRef.current = nextScale > 1.02;
        applyTransform(next.scale, next.x, next.y);
        return;
      }

      if (gesture.mode === "pan" && touches.length === 1) {
        event.preventDefault();
        event.stopPropagation();

        const dx = touches[0].clientX - gesture.lastX;
        const dy = touches[0].clientY - gesture.lastY;
        gesture.lastX = touches[0].clientX;
        gesture.lastY = touches[0].clientY;

        const next = {
          scale: stateRef.current.scale,
          x: stateRef.current.x + dx,
          y: stateRef.current.y + dy,
        };
        stateRef.current = next;
        applyTransform(next.scale, next.x, next.y);
      }
    };

    const onTouchEnd = (event) => {
      const gesture = gestureRef.current;

      if (event.touches.length < 2 && gesture.mode === "pinch") {
        gesture.mode = null;
      }
      if (event.touches.length === 0) {
        gesture.mode = null;
        if (stateRef.current.scale <= 1.02) {
          reset();
        }
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, applyTransform, reset]);

  const isZoomed = useCallback(() => isZoomedRef.current, []);

  return { containerRef, reset, isZoomed };
}
